// The frame around every screen: left nav, the red alarm bar, hand-off toasts, tab title,
// desktop notifications, and the Cmd/Ctrl+K search shortcut.

import { useEffect, useRef } from 'react';
import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ALARM_ACTIONS, shoutingAlarms } from '@bt/core';
import { api, type HealthResponse, type MeResponse } from '../api';
import { useSse, onAppEvent } from '../hooks/useSse';
import { useUsers } from '../hooks/useMe';
import { useToast } from './Toast';
import { displayName, pluralize } from '../lib/format';

export function Shell({ me }: { me: MeResponse }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const users = useUsers();
  const { connected } = useSse(true);

  const counts = useQuery({ queryKey: ['counts'], queryFn: api.conversations.counts, staleTime: 5_000, retry: 1 });
  const health = useQuery({ queryKey: ['health'], queryFn: api.health.get, staleTime: 10_000, retry: 1, refetchInterval: connected ? false : 30_000 });
  // my unread count for the tab title; counts.mine_unread wins when the server sends it
  const mineList = useQuery({
    queryKey: ['mine-unread'],
    queryFn: () => api.conversations.list({ box: 'mine', limit: 50 }),
    enabled: counts.data?.mine_unread === undefined,
    staleTime: 5_000,
    retry: 1,
  });
  const myUnread = counts.data?.mine_unread ?? (mineList.data?.items.filter((c) => c.unread_for_me).length ?? 0);

  useEffect(() => {
    document.title = myUnread > 0 ? `(${myUnread}) SweetText` : 'SweetText';
  }, [myUnread]);

  // Hand-off toast + desktop notification. Remember who each conversation was assigned to so
  // we only toast when it BECOMES mine.
  const assignedRef = useRef<Map<string, string | null>>(new Map());
  const lastFetchRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    return onAppEvent((e) => {
      if (e.type !== 'conversation' || !e.id) return;
      const id = e.id;
      const prev = assignedRef.current.get(id);
      if (e.assigned_user_id !== undefined) assignedRef.current.set(id, e.assigned_user_id ?? null);
      const becameMine = e.assigned_user_id === me.user.id && prev !== me.user.id;
      const hidden = document.visibilityState === 'hidden';
      if (!becameMine && !hidden) return;
      // status updates fire many events per conversation; one look every 10 s is plenty
      const last = lastFetchRef.current.get(id) ?? 0;
      if (!becameMine && Date.now() - last < 10_000) return;
      lastFetchRef.current.set(id, Date.now());
      // one small fetch to name the contact and the person who handed it over
      api.conversations
        .get(id)
        .then((res) => {
          const contact = displayName(res.contact);
          const latest = res.timeline[res.timeline.length - 1];
          if (becameMine) {
            const ev = [...res.timeline].reverse().find((t) => t.type === 'event' && t.event.type === 'assigned');
            const byId = ev && ev.type === 'event' ? ev.event.user_id : null;
            const byName = ev && ev.type === 'event' ? ev.event.user?.name : undefined;
            const who = byName ?? users.find((u) => u.id === byId)?.name ?? 'Someone';
            if (byId !== me.user.id) {
              toast({ text: `${who} handed you ${contact}`, href: `/inbox/${id}` });
              notify(`${who} handed you ${contact}`, ev && ev.type === 'event' ? ev.event.body ?? '' : '');
            }
          } else if (hidden && latest?.type === 'message' && latest.message.direction === 'in') {
            const age = Date.now() - new Date(latest.message.created_at).getTime();
            if (age < 60_000) notify(`New text from ${contact}`, latest.message.body);
          }
        })
        .catch(() => {});
    });
  }, [me.user.id, users, toast]);

  // Cmd/Ctrl+K focuses search (goes to the inbox first when needed)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        const el = document.getElementById('global-search') as HTMLInputElement | null;
        if (el) el.focus();
        else {
          navigate('/inbox');
          window.setTimeout(() => (document.getElementById('global-search') as HTMLInputElement | null)?.focus(), 150);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  // WHY: an acknowledged alarm is "seen, working on it". It stays on the Health page (with who and
  // when) but leaves the red bar and the nav dot; otherwise Acknowledge did nothing visible for an
  // alarm that clears by itself later (stuck_sending, queue_depth).
  const openAlarms = shoutingAlarms(health.data?.alarms);
  const alarmText = openAlarms.length > 0 ? alarmBarText(health.data!) : null;

  const logout = async () => {
    try {
      await api.auth.logout();
    } finally {
      qc.clear();
      navigate('/login', { replace: true });
    }
  };

  return (
    <div className="shell">
      <nav className="nav">
        <div className="brand">SweetText <span className="brand-by">by LeadAlchemy</span></div>
        <NavLink to="/inbox" className={({ isActive }) => `item ${isActive ? 'active' : ''}`}>
          <span>Inbox</span>
          {(counts.data?.unassigned ?? 0) > 0 && <span className="badge" title="Unassigned">{counts.data!.unassigned}</span>}
        </NavLink>
        <NavLink to="/contacts" className={({ isActive }) => `item ${isActive ? 'active' : ''}`}>
          <span>Contacts</span>
        </NavLink>
        <NavLink to="/scheduled" className={({ isActive }) => `item ${isActive ? 'active' : ''}`}>
          <span>Scheduled</span>
          {(counts.data?.needs_attention ?? 0) > 0 && <span className="badge grey" title="Needs attention">{counts.data!.needs_attention}</span>}
        </NavLink>
        <NavLink to="/lists" className={({ isActive }) => `item ${isActive ? 'active' : ''}`}>
          <span>Lists</span>
        </NavLink>
        <NavLink to="/health" className={({ isActive }) => `item ${isActive ? 'active' : ''}`}>
          <span>Health</span>
          {openAlarms.length > 0 && <span className="dot" title={pluralize(openAlarms.length, 'unacknowledged alarm')} />}
        </NavLink>
        <NavLink to="/settings" className={({ isActive }) => `item ${isActive ? 'active' : ''}`}>
          <span>Settings</span>
        </NavLink>
        <div className="spacer" />
        <div className="user">
          <div className="name">{me.user.name}</div>
          <div className="role">{me.user.role === 'admin' ? 'Admin' : 'Agent'}{!connected && ' · live updates off'}</div>
          <button className="btn link small" onClick={logout} style={{ paddingLeft: 0 }}>
            Sign out
          </button>
        </div>
      </nav>
      <div className="main">
        {alarmText && (
          <div className="alarm-bar" role="alert">
            <span>{alarmText}</span>
            <Link to="/health">What to do →</Link>
          </div>
        )}
        <div className="content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

/** "Texting phone has not checked in for 16 min. 7 texts waiting." */
function alarmBarText(h: HealthResponse): string {
  const open = shoutingAlarms(h.alarms);
  const order = ['radio_error', 'line_paused', 'heartbeat_gap', 'phone_offline', 'token_missing', 'queue_depth', 'stuck_sending', 'provider_error', 'webhook_config', 'inbound_error', 'rate_limit_low', 'repetition', 'inbound_silent'];
  const top = [...open].sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type))[0];
  const line = h.lines.find((l) => l.id === top.line_id) ?? h.lines[0];
  const waiting = line?.health ? (line.health.queue_depth_local ?? 0) : h.counters?.queued ?? 0;
  const waitingText = waiting > 0 ? ` ${pluralize(waiting, 'text')} waiting.` : '';
  if ((top.type === 'heartbeat_gap' || top.type === 'phone_offline') && line?.health?.heartbeat_age_s != null) {
    const min = Math.round(line.health.heartbeat_age_s / 60);
    return `Texting phone has not checked in for ${min} min.${waitingText}`;
  }
  const what = top.what ?? top.action?.what ?? ALARM_ACTIONS[top.type]?.what ?? 'A texting problem needs attention.';
  return `${what}${waitingText}`;
}

function notify(title: string, body: string) {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (document.visibilityState !== 'hidden') return;
    new Notification(title, { body: body.slice(0, 120) });
  } catch {
    /* notifications are a nicety */
  }
}
